import type { ReactNode } from 'react';

import PageHeader, { type PageHeaderProps } from './PageHeader';
import { useDefaultPageWidth } from './PageWidthContext';

import styles from './PageLayout.module.scss';

export type PageWidth = 'capped' | 'wide' | 'full';

export interface PageLayoutProps extends PageHeaderProps {
    /**
     * How wide the page may grow: `capped` stops at 1440px, `wide` stops at 2180px for chart-dense
     * pages, `full` fills the screen. Defaults to the nearest `PageWidthProvider`, else `capped`.
     */
    width?: PageWidth;
    /** Applied to the content container. */
    className?: string;
    children?: ReactNode;
}

/**
 * The frame every page renders in: a title row (title, subtitle, actions) above the content.
 * Pages are centred and capped at `--page-max-width` by default; `width="wide"` raises the cap to
 * `--page-wide-max-width` for pages that lay charts out three per row.
 */
export default function PageLayout(props: PageLayoutProps) {
    const defaultWidth = useDefaultPageWidth();
    const { width = defaultWidth, className, children, ...headerProps } = props;

    return (
        <div className={styles.page} data-width={width}>
            <PageHeader {...headerProps} />
            <div className={className}>{children}</div>
        </div>
    );
}
